// CuratePage.jsx - Dedicated URL for the Curate sidebar content
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Search, ArrowLeft, X, Share2 } from "lucide-react";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import GameSearchDropdown from "../common/GameSearchDropdown";
import CurateSimplified from "./CurateSimplified";
import logger from '../../utils/logger';

const DEFAULT_WEIGHT = 0.5;
const DEFAULT_SCOPE = 'all';

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

function CuratePage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const gameIdParam = searchParams.get('gameId');
  const weightParam = searchParams.get('weight');
  const scopeParam = searchParams.get('scope');

  const initialWeight = useMemo(() => {
    const parsed = parseFloat(weightParam);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : DEFAULT_WEIGHT;
  }, [weightParam]);

  const initialScope = useMemo(() => {
    return scopeParam === 'library' ? 'library' : DEFAULT_SCOPE;
  }, [scopeParam]);

  const [selectedGame, setSelectedGame] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(!gameIdParam);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const latestSearchQueryRef = useRef(searchQuery);
  const fetchedGameIdRef = useRef(null);

  const [collection, setCollection] = useState([]);
  const [similarGames, setSimilarGames] = useState([]);
  const [shareStatus, setShareStatus] = useState('');
  const shareTimerRef = useRef(null);

  // Fetch game details when gameId is present in the URL
  useEffect(() => {
    if (!gameIdParam || fetchedGameIdRef.current === gameIdParam) return;
    const numericGameId = parseInt(gameIdParam, 10);
    if (Number.isNaN(numericGameId)) return;

    let cancelled = false;
    fetchedGameIdRef.current = gameIdParam;

    (async () => {
      try {
        const res = await httpRequest(`${API_BASE}/games/${numericGameId}/details`, {
          method: 'GET',
          headers: authService.getAuthHeaders(),
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          const loadedGame = { ...data, id: data.id ?? data.game_id, game_id: data.game_id ?? data.id };
          setSelectedGame(loadedGame);
          setSearchExpanded(false);
        }
      } catch (err) {
        if (!cancelled) {
          logger.warn('[CuratePage] failed to load game from URL param:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
      fetchedGameIdRef.current = null;
    };
  }, [gameIdParam]);

  // Fetch user collection for ownership badges
  useEffect(() => {
    const fetchCollection = async () => {
      if (!user) return;
      try {
        const res = await httpRequest(`${API_BASE}/profile/collection`, {
          method: 'GET',
          headers: authService.getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          const games = Array.isArray(data) ? data : (data.games || []);
          setCollection(games.map(g => ({ ...g, id: g.id || g.game_id })));
        }
      } catch (err) {
        logger.error('[CuratePage] Failed to fetch collection:', err);
        setCollection([]);
      }
    };
    fetchCollection();
  }, [user]);

  const getGameOwnershipStatus = useCallback((gameId) => {
    if (!Array.isArray(collection) || !gameId) return false;
    const target = String(gameId);
    const found = collection.find(game =>
      String(game.game_id) === target || String(game.id) === target
    );
    if (!found) return false;
    const status = (found.ownership_status || '').toLowerCase();
    if (status === 'owned') return 'owned';
    if (status === 'interested') return 'interested';
    if (status === 'excluded') return 'excluded';
    if (status === 'no_shelf') return 'no_shelf';
    return 'no_shelf';
  }, [collection]);

  const debouncedSearch = useMemo(
    () => debounce(async (query) => {
      if (!query || query.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const res = await httpRequest(
          `${API_BASE}/games/search?q=${encodeURIComponent(query.trim())}&limit=15`,
          { method: 'GET', headers: authService.getAuthHeaders() }
        );
        if (query.trim() !== latestSearchQueryRef.current.trim()) return;
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.games || []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        logger.error('[CuratePage] Search failed:', err);
        setSearchResults([]);
      } finally {
        if (query.trim() === latestSearchQueryRef.current.trim()) {
          setIsSearching(false);
        }
      }
    }, 300),
    []
  );

  useEffect(() => {
    latestSearchQueryRef.current = searchQuery;
    if (searchQuery) {
      debouncedSearch(searchQuery);
      setIsDropdownVisible(true);
    } else {
      setSearchResults([]);
      setIsDropdownVisible(false);
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearch]);

  const updateUrlParams = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleGameSelect = useCallback((game) => {
    const gameId = game.id ?? game.game_id;
    setSelectedGame(game);
    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownVisible(false);
    setSearchExpanded(false);
    updateUrlParams({ gameId });
  }, [updateUrlParams]);

  const handleClearGame = useCallback(() => {
    setSelectedGame(null);
    setSearchExpanded(true);
    updateUrlParams({ gameId: null });
  }, [updateUrlParams]);

  const handleSettingsChange = useCallback(({ weight, scope }) => {
    const updates = {};
    // Keep the parameters in the URL so that shared/bookmarked links stay stable.
    if (weight !== null && weight !== undefined) {
      updates.weight = parseFloat(Number(weight).toFixed(2));
    }
    if (scope !== null && scope !== undefined) {
      updates.scope = scope;
    }
    updateUrlParams(updates);
  }, [updateUrlParams]);

  const backgroundLocation = location.state?.backgroundLocation;

  const handleBack = useCallback(() => {
    if (backgroundLocation) {
      navigate(`${backgroundLocation.pathname}${backgroundLocation.search || ''}`, {
        replace: false,
      });
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [backgroundLocation, navigate]);

  const handleShareResults = useCallback(async () => {
    const weight = searchParams.get('weight') ?? DEFAULT_WEIGHT;
    const scope = searchParams.get('scope') ?? DEFAULT_SCOPE;
    const weightLabel = Number(weight) < 0.35
      ? 'Feel-focused'
      : Number(weight) > 0.65
        ? 'Mechanics-focused'
        : 'Balanced';
    const scopeLabel = scope === 'library' ? 'My library' : 'All games';
    const gameList = similarGames
      .slice(0, 10)
      .map((g, i) => `${i + 1}. ${g.name || g.game_name}`)
      .join('\n');
    const text = `Similar games to ${selectedGame?.name || 'this game'} on Pista\n\nReference: ${selectedGame?.name || 'Unknown'}\nWeight: ${weightLabel} (${Number(weight).toFixed(2)})\nScope: ${scopeLabel}${gameList ? `\n\n${gameList}` : ''}\n\n${window.location.href}`;

    try {
      if (navigator?.share) {
        await navigator.share({ title: 'Pista similar games', text });
        setShareStatus('Shared!');
      } else {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      }
    } catch {
      setShareStatus('Share not available');
    }
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    shareTimerRef.current = setTimeout(() => setShareStatus(''), 2000);
  }, [searchParams, selectedGame, similarGames]);

  useEffect(() => () => {
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
  }, []);

  const searchPlaceholder = selectedGame?.name
    ? `Search for any board game (see ${selectedGame.name})`
    : "Search for any board game…";

  return (
    <div
      className="curate-page"
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--bg-primary, #fafbfc)',
        color: 'var(--text-primary, #333)',
        fontFamily: 'var(--font-family, system-ui, -apple-system, sans-serif)',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'var(--surface-color, #fff)',
          borderBottom: '1px solid var(--border-color, #e0e0e0)',
          padding: '0.75rem 1rem',
        }}
      >
        <div
          style={{
            maxWidth: '720px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <button
            type="button"
            className="back-button"
            onClick={handleBack}
            aria-label="Go back"
            title="Go back"
            style={{
              width: '40px',
              height: '40px',
              padding: 0,
              backgroundColor: 'var(--success-color, #4caf50)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={18} />
          </button>

          <h1
            style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              margin: 0,
              flex: 1,
              color: 'var(--text-primary, #333)',
            }}
          >
            Game Discovery
            <span
              style={{
                fontSize: '0.65em',
                padding: '0.15em 0.4em',
                borderRadius: '4px',
                background: 'var(--primary-color, #1976d2)',
                color: 'white',
                fontWeight: 500,
                marginLeft: '0.5rem',
                verticalAlign: 'middle',
              }}
            >
              beta
            </span>
          </h1>

          {selectedGame && (
            <button
              type="button"
              onClick={handleShareResults}
              aria-label="Share similar games"
              title="Share similar games"
              style={{
                backgroundColor: 'var(--pista-green-primary, #2E7D32)',
                color: 'var(--pista-button-text, white)',
                border: '2px solid var(--pista-green-primary, #2E7D32)',
                borderRadius: '8px',
                padding: 'clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)',
                fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(46, 125, 50, 0.3)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                minHeight: '40px',
                minWidth: '40px',
                flexShrink: 0,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--pista-green-hover, #388E3C)';
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 125, 50, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--pista-green-primary, #2E7D32)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(46, 125, 50, 0.3)';
              }}
            >
              <Share2 size={16} />
              {shareStatus || 'Share'}
            </button>
          )}

          {selectedGame && !searchExpanded && (
            <button
              type="button"
              onClick={() => setSearchExpanded(true)}
              aria-label="Search for a game"
              title="Search for a game"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: '1px solid var(--border-color, #ddd)',
                backgroundColor: 'transparent',
                color: 'var(--text-primary, #333)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <Search size={18} />
            </button>
          )}
        </div>

        {/* Search bar */}
        {searchExpanded && (
          <div
            style={{
              maxWidth: '720px',
              margin: '0.75rem auto 0',
            }}
          >
            <GameSearchDropdown
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isVisible={isDropdownVisible}
              onVisibilityChange={setIsDropdownVisible}
              searchResults={searchResults}
              isLoading={isSearching}
              onGameSelect={handleGameSelect}
              showImages={true}
              placeholder={searchPlaceholder}
              autoFocus={true}
              trailingSlot={
                selectedGame ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearGame();
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="Clear selected game"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary, #666)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={18} />
                  </button>
                ) : searchQuery.trim() ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchQuery('');
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="Clear search"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary, #666)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={18} />
                  </button>
                ) : (
                  <span style={{ display: 'flex', color: 'var(--text-secondary, #666)' }}>
                    <Search size={18} />
                  </span>
                )
              }
            />
            {selectedGame && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary, #666)' }}>
                Selected: <strong>{selectedGame.name}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Curate content */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '1rem' }}>
        <CurateSimplified
          standalone={true}
          user={user}
          onClose={handleBack}
          initialMode={selectedGame ? 'SIMILARITY' : null}
          baseGame={selectedGame}
          getGameOwnershipStatus={getGameOwnershipStatus}
          initialWeight={initialWeight}
          initialScope={initialScope}
          onSettingsChange={handleSettingsChange}
          onResultsLoaded={setSimilarGames}
        />
      </div>
    </div>
  );
}

export default CuratePage;
